import { Theme } from '@material-ui/core/styles'
import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ContactSupportLink, LogOutPrompt } from '../Common'
import { Typography, Box, Stack, ClickAwayListener, MenuList, Menu } from '@mui/material'

import PersonOutline from '../../assets/images/icons/person_outline.svg?react'
import Settings from '../../assets/images/icons/settings.svg?react'
import Logout from '../../assets/images/icons/logout.svg?react'
import { APP_PATHS, PATH_PARAMS } from '../../routes/paths'
import { ContractStatusType, Individual } from '../../graphql'
import { useCustomHistory, useIsLimitedContract, useLogOut } from '../../hooks'
import { useReactiveVar } from '@apollo/client'
import { isDirtyForm } from '../../graphql/local'
import { useTableSort } from '../../hooks/useCurrentTableSort'
import { ContractSupportItem, ProfileMenuItem, ProfileMenuItemText } from './StyledComponents'

export const DashboardNavMenu: FC<{
  anchorEl: null | HTMLElement
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>
  user: Individual
  contractId: number | string | undefined
  limitMode: boolean
}> = ({ anchorEl, setAnchorEl, user, contractId, limitMode }) => {
  const { t } = useTranslation()
  const handleLogOut = useLogOut()
  const { push } = useCustomHistory()

  const { clearTablesSort } = useTableSort()
  const isDirtyField = useReactiveVar(isDirtyForm)
  const { contractStatus } = useIsLimitedContract()
  const [logoutPromptOpen, setLogoutPromptOpen] = useState(false)

  const checkableContractStatuses = [
    ContractStatusType.Active,
    ContractStatusType.Suspended,
    ContractStatusType.TerminationPending,
    ContractStatusType.Monitoring,
  ]

  const contractStatusCheck = contractStatus && checkableContractStatuses.includes(contractStatus)

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const onLogout = useCallback(() => {
    if (isDirtyField) {
      setLogoutPromptOpen(true)
    } else {
      void handleLogOut()
    }
  }, [isDirtyField])

  const redirectToPersonalProfile = () => {
    clearTablesSort()
    push(
      generatePath(APP_PATHS.dashboard.personProfile, {
        [PATH_PARAMS.applicationId]: contractId,
      }),
    )
  }

  const redirectToSettings = () => {
    clearTablesSort()
    push(
      generatePath(APP_PATHS.dashboard.settings, {
        [PATH_PARAMS.applicationId]: contractId,
      }),
    )
  }

  return (
    <Stack
      position="relative"
      justifyContent="center"
      alignItems="center"
      sx={{ cursor: 'pointer' }}
    >
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 1,
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <MenuList data-test="personalMenuList">
            <Box textAlign="center" borderBottom="1px solid black" p={1.5} display={{ md: 'none' }}>
              <Typography variant="h5">
                {user.firstName} {user.lastName}
              </Typography>
            </Box>

            <ProfileMenuItem disabled={limitMode} onClick={handleClose}>
              <ProfileMenuItemText onClick={redirectToPersonalProfile}>
                <PersonOutline />
                <Typography variant="body1">{t('personalProfile')}</Typography>
              </ProfileMenuItemText>
            </ProfileMenuItem>

            <ProfileMenuItem onClick={handleClose}>
              <ProfileMenuItemText onClick={redirectToSettings}>
                <Settings />
                <Typography variant="body1">{t('settings', 'Settings')}</Typography>
              </ProfileMenuItemText>
            </ProfileMenuItem>

            {contractStatusCheck && (
              <ContractSupportItem onClick={handleClose}>
                <ProfileMenuItemText
                  sx={{
                    '& svg': {
                      mr: (theme: Theme) => theme.spacing(1.5),
                    },
                  }}
                >
                  <ContactSupportLink menuLink={true} footerDisplay={true} newTab />
                </ProfileMenuItemText>
              </ContractSupportItem>
            )}

            <ProfileMenuItem>
              <ProfileMenuItemText onClick={onLogout}>
                <Logout />
                <Typography variant="body1">{t('logOut', 'Log Out')}</Typography>
              </ProfileMenuItemText>
            </ProfileMenuItem>
          </MenuList>
        </ClickAwayListener>
      </Menu>

      <LogOutPrompt logoutPromptOpen={logoutPromptOpen} setLogoutPromptOpen={setLogoutPromptOpen} />
    </Stack>
  )
}
